import { format } from 'date-fns'
import usePlan from './usePlan'

export default function useFilters(model) {
  const { pills, filterContext, profile } = model
  //const { getPlan, getPlans } = usePlan(profile.value, filterContext.value)
  const nextIndex = computed(() => pills.value.length)
  const language = computed(() => filterContext.value.language)
  //const languages = computed(() => model.languages.value.value.items)
  const asOfDate = computed(() => filterContext.value.asOfDate)
  const distance = computed(() => filterContext.value.distance)
  const gender = computed(() => filterContext.value.gender)
  const careNetworkId = computed(() => filterContext.value.careNetworkId)
  const acceptingNewPatients = computed(() => filterContext.value.acceptingNewPatients)
  const networkIndex = computed(() => filterContext.value.networkIndex)
  const benefitLevel = computed(() => filterContext.value.tier)
  const cc = computed(() => filterContext.value.cc)
  const code = computed(() => filterContext.value.code)
  const hLabel = computed(() => filterContext.value.hLabel)
  const hCode = computed(() => filterContext.value.hCode)

  const specialties = computed(() => model.specialties.value)
  const procedures = computed(() => model.procedures.value)

  /**
   * This is the pills driver build function.  We will rebuild the pills each time something changes in the
   * filters.
   */
  const buildPills = () => {
    const newPills = []
    buildNetwork(newPills)
    buildBenefitLevels(newPills)
    //buildAsofDate(newPills)
    buildDistance(newPills)
    buildGender(newPills)
    buildCareNetworkClinic(newPills)
    buildAcceptingNewPatients(newPills)
    buildHospitalAffiliations(newPills)
    //buildLanguage(newPills)
    buildSpecialties(newPills)
    buildProcedure(newPills)
    pills.value = newPills
  }

  const buildNetwork = (pills) => {
    //Re-execute usePlan with the latest state of the filterContext.
    const { getPlan, getPlans } = usePlan(profile.value, filterContext.value)
    const plans = getPlans()
    if (getPlans().length > 1 && filterContext.value.networkIndex > 0) {
      const plan = getPlan()
      pills.push({ pillId: `networkIndex:${networkIndex.value}`, text: `Network: ${plan.name}`, index: nextIndex.value })
    }
  }

  /**
   * Apply appropriate logic to determine the benefit lebel label.  Applies the special case when
   * the tierLabel on the plan is "Benefit Tier"
   */
  const benefitLevelLabel = (tier) => {
    //Re-execute usePlan with the latest state of the filterContext.
    const { getPlan } = usePlan(profile.value, filterContext.value)
    const plan = getPlan()
    if (plan.tierLabel == 'Benefit Tier') {
      if (tier == 1) return `Select`
      if (tier == 2) return `Standard`
    }
    return `${plan.tierLabel} ${tier}`
  }

  const buildBenefitLevels = (pills) => {
    if (filterContext.value.tier) {
      const label = benefitLevelLabel(filterContext.value.tier)
      pills.push({ pillId: `benefitLevel:${filterContext.value.tier}`, text: label, index: nextIndex.value })
    }
  }

  const buildSpecialties = (pills) => {
    if (filterContext.value.cc && filterContext.value.cc.length > 0 && model.specialties.value) {
      filterContext.value.cc.forEach((s) => {
        //Find the specialty in the list of available specialties in the model.
        const specialty = model.specialties.value.find((sp) => sp.id == s)
        if (specialty) pills.push({ pillId: `specialty:${s}`, text: `${specialty.name}`, index: nextIndex.value })
      })
    }
  }

  const buildProcedure = (pills) => {
    if (filterContext.value.code && model.procedures.value) {
      const proc = model.procedures.value.find((p) => p.truvenCode == filterContext.value.code)
      if (proc) pills.push({ pillId: `procedure:${filterContext.value.code}`, text: `${proc.name}`, index: nextIndex.value })
    }
  }

  const buildLanguage = (pills) => {
    const lang = language.value ? languages.value.find((l) => l.value == language.value) : null
    if (lang) {
      pills.push({ pillId: `language:${lang.value}`, text: lang.label, index: nextIndex.value })
    }
  }

  // const buildAsofDate = (pills) => {
  //   const today = formatDate(new Date())

  //   if (!profile.value.isAuthenticated && context.value.asOfDate != today) {
  //     pills.push({ pillId: `asOfDate:${context.value.asOfDate}`, text: `As of ${context.value.asOfDate}`, index: nextIndex.value })
  //   }
  // }

  const buildDistance = (pills) => {
    if (filterContext.value.distance != 20) {
      pills.push({ pillId: `distance:${filterContext.value.distance}`, text: `Within ${filterContext.value.distance} miles`, index: nextIndex.value })
    }
  }

  const buildGender = (pills) => {
    if (filterContext.value.gender) {
      pills.push({ pillId: `gender:${filterContext.value.gender}`, text: `Gender: ${filterContext.value.gender}`, index: nextIndex.value })
    }
  }

  const buildCareNetworkClinic = (pills) => {
    if (filterContext.value.careNetworkId && filterContext.value.careNetworkName) {
      pills.push({
        pillId: `careNetworkId:${filterContext.value.careNetworkId}`,
        text: `${filterContext.value.careNetworkName} care network`,
        index: nextIndex.value,
      })
    }
  }

  const buildAcceptingNewPatients = (pills) => {
    if (filterContext.value.acceptingNewPatients) {
      pills.push({
        pillId: `acceptingNewPatients:${filterContext.value.acceptingNewPatients}`,
        text: `Accepting new patients`,
        index: nextIndex.value,
      })
    }
  }

  const buildHospitalAffiliations = (pills) => {
    // if (filterContext.value.hLabel && filterContext.value.hCode) {
    //   pills.push({ pillId: `hospitalAffiliation:${filterContext.value.hCode}`, text: filterContext.value.hLabel, index: nextIndex.value })
    // }
  }

  const formatDate = (date) => {
    return format(date, 'yyyy-MM-dd')
  }

  /**
   * When we build out the pill data, each pill has a pillId which we build using a colon delimited format.
   * ( ${type}:${value} )
   * This function breaks down an incoming pill down into its informational parts - type and value.
   */
  const pillData = (pill) => {
    const parts = pill.pillId.split(':')
    const type = parts[0]
    const value = parts[1]

    return { type, value }
  }

  /**
   * Removes a pill from the pill list. To do this, we place the filterContext to the correct state with the pill removed.
   * Sometimes, this means going back to the 'default' state. At other times - if the filter is a mulit-select, then
   * we need to remove the item represented by the pill from the filterContext's list.
   */
  const removePill = (pill) => {
    const { type, value } = pillData(pill)
    if (type == 'language') filterContext.value.language = ''
    if (type == 'asOfDate') filterContext.value.asOfDate = formatDate(new Date())
    if (type == 'distance') filterContext.value.distance = 20
    if (type == 'gender') filterContext.value.gender = ''
    if (type == 'careNetworkId') filterContext.value.careNetworkId = ''
    if (type == 'acceptingNewPatients') filterContext.value.acceptingNewPatients = ''
    if (type == 'hospitalAffiliation')
      (filterContext.value.hCode = ''), (filterContext.value.hLabel = ''), (filterContext.value.hospitalAffiliation = '')
    if (type == 'networkIndex') filterContext.value.networkIndex = 0
    if (type == 'benefitLevel') filterContext.value.tier = ''
    if (type == 'procedure') filterContext.value.code = ''

    //Update the cc list in filterContext by removing the item represented by the pill.
    if (type == 'specialty') filterContext.value.cc = filterContext.value.cc.filter((c) => c != value)
    buildPills()
  }

  /**
   * Watch for all the filterContext attributes changes that we need to respond to.  These are all attributes modified by filters.
   */
  watch(
    [
      language,
      asOfDate,
      distance,
      gender,
      careNetworkId,
      acceptingNewPatients,
      networkIndex,
      benefitLevel,
      cc,
      code,
      hLabel,
      hCode,
      specialties,
      procedures,
    ],
    () => {
      buildPills()
    }
  )

  return {
    buildPills,
    removePill,
    pillData,
    formatDate,
    buildAcceptingNewPatients,
    buildHospitalAffiliations,
    buildGender,
    buildCareNetworkClinic,
    buildDistance,
    //buildAsofDate,
    buildLanguage,
    buildProcedure,
    buildSpecialties,
    buildBenefitLevels,
    buildNetwork,
    benefitLevelLabel,
  }
}